import React, { useMemo } from "react";
import {
  FaExternalLinkAlt,
  FaFileInvoice,
  FaFileSignature,
  FaReceipt,
} from "react-icons/fa";

import {
  computeProgress,
  formatDateFR,
  formatEuro,
  getPeriodicityMeta,
  getStatusMeta,
} from "../utils/subscriptionDashboardUtils";

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function isQuotePending(subscription) {
  const key = normalizeKey(subscription?.status);

  return (
    key.includes("devis") || key.includes("signature") || key.includes("quote")
  );
}

function getQuoteStatusMeta(status) {
  const key = normalizeKey(status);

  if (
    key.includes("devis") ||
    key.includes("signature") ||
    key.includes("quote")
  ) {
    return {
      label: "Devis à signer",
      badge:
        "bg-purple-50 text-purple-700 ring-purple-100 dark:bg-purple-500/10 dark:text-purple-200 dark:ring-purple-500/20",
      row: "hover:bg-purple-50/60 dark:hover:bg-purple-950/20",
    };
  }

  return getStatusMeta(status);
}

function ProgressGauge({ progress }) {
  if (!progress?.valid) {
    return <span className="italic text-slate-400">—</span>;
  }

  return (
    <div className="min-w-[190px]">
      <div className="mb-1 flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-300">
        <span className="font-bold">{progress.text}</span>
        <span className="tabular-nums">{progress.percent}%</span>
      </div>

      <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${progress.barClass}`}
          style={{ width: `${progress.percent}%` }}
        />
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaReceipt />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun abonnement trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Contactez le support si vous pensez que c’est une erreur.
        </p>
      </div>
    </section>
  );
}

function DevisTable({ devis }) {
  if (!devis.length) return null;

  return (
    <div className="mb-8">
      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-purple-500 dark:text-purple-300">
            Devis
          </p>

          <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            Devis à signer
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Retrouvez ici les devis en attente de signature ou de validation.
          </p>
        </div>

        <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-purple-700 ring-1 ring-purple-100 dark:bg-purple-500/10 dark:text-purple-200 dark:ring-purple-500/20">
          {devis.length} devis
        </span>
      </div>

      <div className="overflow-x-auto rounded-[22px] border border-purple-200 bg-purple-50/40 dark:border-purple-900/50 dark:bg-purple-950/10">
        <table className="w-full min-w-[960px]">
          <thead className="bg-purple-50 text-xs uppercase tracking-[0.14em] text-slate-600 dark:bg-purple-950/30 dark:text-slate-300">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Produit</th>
              <th className="px-4 py-3 text-right font-bold">Montant</th>
              <th className="px-4 py-3 text-left font-bold">Périodicité</th>
              <th className="px-4 py-3 text-left font-bold">Période prévue</th>
              <th className="px-4 py-3 text-left font-bold">Statut</th>
              <th className="px-4 py-3 text-right font-bold">Devis</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-purple-100 text-sm dark:divide-purple-900/40">
            {devis.map((subscription) => {
              const status = getQuoteStatusMeta(subscription.status);
              const periodicity = getPeriodicityMeta(subscription.periodicity);

              return (
                <tr
                  key={`devis-${subscription.id}`}
                  className={`transition ${status.row}`}
                >
                  <td className="px-4 py-4 align-top">
                    <p className="font-bold text-slate-900 dark:text-white">
                      {subscription.product || "Devis"}
                    </p>

                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Référence #{subscription.id}
                    </p>
                  </td>

                  <td className="px-4 py-4 text-right align-top">
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {formatEuro(subscription.total_amount)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${periodicity.badge}`}
                    >
                      {periodicity.label}
                    </span>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {formatDateFR(subscription.start_date)}
                      <span className="mx-2 text-slate-400">→</span>
                      {formatDateFR(subscription.end_date)}
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${status.badge}`}
                    >
                      {status.label}
                    </span>
                  </td>

                  <td className="px-4 py-4 text-right align-top">
                    {subscription.drive_quote_link ? (
                      <a
                        href={subscription.drive_quote_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-purple-200 bg-white px-3 py-2 text-xs font-bold text-purple-700 transition hover:bg-purple-50 dark:border-purple-800 dark:bg-slate-900 dark:text-purple-200 dark:hover:bg-purple-950/30"
                      >
                        <FaFileSignature />
                        Signer le devis
                        <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    ) : (
                      <span className="text-xs italic text-slate-400">
                        Devis non disponible
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FacturesTable({ subscriptions }) {
  if (!subscriptions.length) return null;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Factures
          </p>

          <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            Abonnements et factures
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Suivez la période de validité, le statut, la progression et l’accès
            à la facture.
          </p>
        </div>

        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {subscriptions.length} abonnement(s)
        </span>
      </div>

      <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
        <table className="w-full min-w-[1100px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Produit</th>
              <th className="px-4 py-3 text-right font-bold">Montant</th>
              <th className="px-4 py-3 text-left font-bold">Périodicité</th>
              <th className="px-4 py-3 text-left font-bold">Période</th>
              <th className="px-4 py-3 text-left font-bold">Statut</th>
              <th className="px-4 py-3 text-left font-bold">Progression</th>
              <th className="px-4 py-3 text-right font-bold">Facture</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {subscriptions.map((subscription) => {
              const status = getStatusMeta(subscription.status);
              const periodicity = getPeriodicityMeta(subscription.periodicity);
              const progress = computeProgress(
                subscription.start_date,
                subscription.end_date,
              );

              return (
                <tr
                  key={subscription.id}
                  className={`transition ${status.row}`}
                >
                  <td className="px-4 py-4 align-top">
                    <p className="font-bold text-slate-900 dark:text-white">
                      {subscription.product || "Abonnement"}
                    </p>

                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Référence #{subscription.id}
                    </p>
                  </td>

                  <td className="px-4 py-4 text-right align-top">
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {formatEuro(subscription.total_amount)}
                    </p>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${periodicity.badge}`}
                    >
                      {periodicity.label}
                    </span>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {formatDateFR(subscription.start_date)}
                      <span className="mx-2 text-slate-400">→</span>
                      {formatDateFR(subscription.end_date)}
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${status.badge}`}
                    >
                      {status.label}
                    </span>
                  </td>

                  <td className="px-4 py-4 align-top">
                    <ProgressGauge progress={progress} />
                  </td>

                  <td className="px-4 py-4 text-right align-top">
                    {subscription.drive_invoice_link ? (
                      <a
                        href={subscription.drive_invoice_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <FaFileInvoice />
                        Facture
                        <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    ) : (
                      <span className="text-xs italic text-slate-400">
                        Non disponible
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SubscriptionsTable({ subscriptions = [], loading }) {
  const { devis, factures } = useMemo(() => {
    const rows = Array.isArray(subscriptions) ? subscriptions : [];

    return rows.reduce(
      (acc, subscription) => {
        if (isQuotePending(subscription)) {
          acc.devis.push(subscription);
        } else {
          acc.factures.push(subscription);
        }

        return acc;
      },
      {
        devis: [],
        factures: [],
      },
    );
  }, [subscriptions]);

  if (loading) return <SkeletonRows />;

  if (!subscriptions.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-6 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Abonnements
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Détails de vos abonnements
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Les devis à signer et les factures sont maintenant affichés dans
              deux blocs distincts.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {devis.length > 0 ? (
              <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-purple-700 ring-1 ring-purple-100 dark:bg-purple-500/10 dark:text-purple-200 dark:ring-purple-500/20">
                {devis.length} devis
              </span>
            ) : null}

            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {factures.length} facture{factures.length > 1 ? "s" : ""}
            </span>
          </div>
        </div>

        <DevisTable devis={devis} />

        <FacturesTable subscriptions={factures} />
      </div>
    </section>
  );
}
